import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import {
  getGroupSoapTemplates,
  updateGroupSoapTemplates,
  getDrugSoapTemplates,
  updateDrugSoapTemplates,
  suggestKeywords,
} from '@/features/internal/drugs/apis/soapTemplate'
import {
  SoapTemplateGroup,
  SoapTemplatesUpdateRequest,
} from '@/features/internal/drugs/types/soapTemplate'
import { useToast } from '@/shared/contexts/ToastContext'

/**
 * テンプレートアイテムにIDがない場合、一時的なIDを生成する
 */
const ensureTemplateIds = (
  groups: SoapTemplateGroup[]
): SoapTemplateGroup[] => {
  return groups.map((group) => ({
    ...group,
    templates: group.templates.map((item) => {
      if (!item.id) {
        return {
          ...item,
          id: `temp-item-${Date.now()}-${Math.random()
            .toString(36)
            .substr(2, 9)}`,
        }
      }
      return item
    }),
  }))
}

// ========================================
// グループ用クエリ
// ========================================

interface UseGroupSoapTemplatesQueryReturn {
  templateGroups: SoapTemplateGroup[]
  data: SoapTemplateGroup[] | undefined
  isLoading: boolean
  isError: boolean
  error: Error | null
  refetch: () => void
  isFetching: boolean
  isSuccess: boolean
}

/**
 * 医薬品グループのSOAPテンプレート取得クエリ
 */
export const useGroupSoapTemplatesQuery = (
  groupId: string | undefined
): UseGroupSoapTemplatesQueryReturn => {
  const query = useQuery({
    queryKey: queryKeys.drugGroups.soaps(groupId ?? ''),
    queryFn: async (): Promise<SoapTemplateGroup[]> => {
      if (!groupId || groupId === 'create') {
        return []
      }
      const response = await getGroupSoapTemplates(groupId)
      return ensureTemplateIds(response.data)
    },
    enabled: !!groupId && groupId !== 'create',
    staleTime: 1000 * 60 * 5, // 5分キャッシュ
  })

  return {
    templateGroups: query.data ?? [],
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    isFetching: query.isFetching,
    isSuccess: query.isSuccess,
  }
}

/**
 * 医薬品グループのSOAPテンプレート更新 Mutation
 */
export const useUpdateGroupSoapTemplatesMutation = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: async ({
      groupId,
      templateGroups,
    }: {
      groupId: string
      templateGroups: SoapTemplateGroup[]
    }) => {
      const data: SoapTemplatesUpdateRequest = {
        template_groups: templateGroups,
      }
      const response = await updateGroupSoapTemplates(groupId, data)
      return ensureTemplateIds(response.data)
    },
    onSuccess: (data, { groupId }) => {
      showToast('SOAPテンプレートを更新しました', 'success')
      // キャッシュを更新
      queryClient.setQueryData(queryKeys.drugGroups.soaps(groupId), data)
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      const errorMessage =
        error.response?.data?.detail || 'SOAPテンプレートの更新に失敗しました'
      showToast(errorMessage, 'error')
    },
  })
}

// ========================================
// 医薬品用クエリ
// ========================================

interface UseDrugSoapTemplatesQueryReturn {
  templateGroups: SoapTemplateGroup[]
  data: SoapTemplateGroup[] | undefined
  isLoading: boolean
  isError: boolean
  error: Error | null
  refetch: () => void
  isFetching: boolean
  isSuccess: boolean
}

/**
 * 医薬品のSOAPテンプレート取得クエリ
 */
export const useDrugSoapTemplatesQuery = (
  drugId: string | undefined
): UseDrugSoapTemplatesQueryReturn => {
  const query = useQuery({
    queryKey: queryKeys.drugs.soaps(drugId ?? ''),
    queryFn: async (): Promise<SoapTemplateGroup[]> => {
      if (!drugId || drugId === 'create') {
        return []
      }
      const response = await getDrugSoapTemplates(drugId)
      return ensureTemplateIds(response.data)
    },
    enabled: !!drugId && drugId !== 'create',
    staleTime: 1000 * 60 * 5, // 5分キャッシュ
  })

  return {
    templateGroups: query.data ?? [],
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    isFetching: query.isFetching,
    isSuccess: query.isSuccess,
  }
}

/**
 * 医薬品のSOAPテンプレート更新 Mutation
 */
export const useUpdateDrugSoapTemplatesMutation = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: async ({
      drugId,
      templateGroups,
    }: {
      drugId: string
      templateGroups: SoapTemplateGroup[]
    }) => {
      const data: SoapTemplatesUpdateRequest = {
        template_groups: templateGroups,
      }
      const response = await updateDrugSoapTemplates(drugId, data)
      return ensureTemplateIds(response.data)
    },
    onSuccess: (data, { drugId }) => {
      showToast('SOAPテンプレートを更新しました', 'success')
      // キャッシュを更新
      queryClient.setQueryData(queryKeys.drugs.soaps(drugId), data)
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      const errorMessage =
        error.response?.data?.detail || 'SOAPテンプレートの更新に失敗しました'
      showToast(errorMessage, 'error')
    },
  })
}

// ========================================
// キーワード提案 Mutation
// ========================================

/**
 * キーワード提案のフォールバックデータ
 */
const KEYWORD_SUGGESTION_FALLBACK = {
  analysisContent: [
    'キーワード提案サービスに接続できませんでした',
    '基本的なキーワードを提案します',
  ],
  suggestedKeywords: ['投薬指導', '副作用', '服薬指導'],
}

/**
 * キーワード提案 Mutation
 *
 * エラー時はフォールバックデータを返してユーザー体験を維持
 */
export const useKeywordSuggestionMutation = () => {
  return useMutation({
    mutationFn: async (groupId: string) => {
      const response = await suggestKeywords(groupId)
      return response.data.data
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      // エラー時はトースト表示せず、フォールバックで対応
      // （呼び出し側でフォールバックデータを使用）
      console.warn('キーワード提案API失敗:', error.message)
    },
  })
}

/**
 * キーワード提案のフォールバックデータを取得
 */
export const getKeywordSuggestionFallback = () => KEYWORD_SUGGESTION_FALLBACK
